// src/utils/resolveDeviceName.js
// Shared resolver for a human-readable, localized label for the device behind a
// verification request. Used by BOTH the toast path (verificationRequestManager)
// and the modal path (VerificationCoordinator) so the label is computed identically
// in both places and only needs to be maintained once.

import logger from './logger';

// Number of leading device-id characters used in the fallback label.
const DEVICE_ID_LABEL_LENGTH = 8;

/**
 * Resolve a localized display name for the device making a verification request.
 *
 * @param {Object} request - Matrix verification request object
 * @param {Object} deps
 * @param {Object} deps.client - Matrix client (may be null/undefined)
 * @param {Function} deps.t - i18n translate function: (key, vars, defaultValue) => string
 * @returns {Promise<string>} Localized device label, never empty
 */
export async function resolveDeviceName(request, { client, t }) {
  const unknownDevice = () => t('VerificationCoordinator.unknownDevice', {}, 'Unknown Device');

  try {
    if (!client) {
      return unknownDevice();
    }

    const crypto = client.getCrypto?.();
    if (!crypto) {
      return unknownDevice();
    }

    const userId = request.otherUserId || request.userId;
    const deviceId = request.otherDeviceId || request.deviceId;

    if (!userId || !deviceId) {
      return unknownDevice();
    }

    // Try to get device info using correct Rust crypto API.
    // Optional chaining guards SDK shape variance between Rust and legacy crypto.
    const userDevices = await crypto.getUserDeviceInfo?.([userId]);
    const device = userDevices?.get?.(userId)?.get?.(deviceId);

    if (device?.display_name || device?.displayName) {
      return device.display_name || device.displayName;
    }

    // Fallback to a localized "Device <id>" label when the session has no name set.
    const idShort = deviceId.substring(0, DEVICE_ID_LABEL_LENGTH);
    return t('VerificationCoordinator.deviceFallback', { id: idShort }, `Device ${idShort}`);
  } catch (error) {
    logger.warn('resolveDeviceName: Could not resolve device name:', error);
    return unknownDevice();
  }
}

export default resolveDeviceName;
